import { Chess } from 'chess.js';

export { Chess };

import type {
  ICastlingRights,
  IMove,
  IRowCol,
  Piece,
  PieceSymbol,
  Square,
  TBoard,
} from './types';
import type { IChessStateFen } from './api-types';
import { DEFAULT_CASTLING_RIGHTS } from './types';

export type TChessInstance = InstanceType<typeof Chess>;

const FILES = 'abcdefgh';

export function positionToSquare(row: number, col: number): Square {
  return `${FILES[col]}${row + 1}` as Square;
}

export function squareToPosition(square: Square): { row: number; col: number } {
  const col = FILES.indexOf(square[0]);
  const row = Number(square[1]) - 1;
  return { row, col };
}

function pieceToFenChar(piece: Piece): string {
  return piece.color === 'w' ? piece.type.toUpperCase() : piece.type;
}

export function stateToFen(state: {
  board: TBoard;
  currentPlayer: 0 | 1;
  castlingRights?: ICastlingRights;
  moveCount?: number;
}): string {
  const board = state.board;
  const ranks: string[] = [];
  for (let r = 7; r >= 0; r--) {
    let rank = '';
    let empty = 0;
    for (let c = 0; c < 8; c++) {
      const cell = board[r][c];
      if (cell) {
        if (empty > 0) {
          rank += empty;
          empty = 0;
        }
        rank += pieceToFenChar(cell);
      } else {
        empty++;
      }
    }
    if (empty > 0) rank += empty;
    ranks.push(rank);
  }
  const piecePlacement = ranks.join('/');
  const turn = state.currentPlayer === 0 ? 'w' : 'b';
  const cr = state.castlingRights ?? DEFAULT_CASTLING_RIGHTS;
  let castling = '';
  if (cr.whiteKingside) castling += 'K';
  if (cr.whiteQueenside) castling += 'Q';
  if (cr.blackKingside) castling += 'k';
  if (cr.blackQueenside) castling += 'q';
  if (!castling) castling = '-';
  const halfmove = 0;
  const fullmove = Math.max(1, 1 + Math.floor((state.moveCount ?? 0) / 2));
  return `${piecePlacement} ${turn} ${castling} - ${halfmove} ${fullmove}`;
}

export function castlingFromChess(chess: TChessInstance): ICastlingRights {
  const fen = chess.fen();
  const castlingStr = fen.split(' ')[2] ?? '-';
  return {
    whiteKingside: castlingStr.includes('K'),
    whiteQueenside: castlingStr.includes('Q'),
    blackKingside: castlingStr.includes('k'),
    blackQueenside: castlingStr.includes('q'),
  };
}

export function boardFromChess(chess: TChessInstance): TBoard {
  const cb = chess.board();
  const board: TBoard = Array(8)
    .fill(null)
    .map(() => Array(8).fill(null));
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const ourRow = 7 - r;
      const cell = cb[r][c];
      if (cell) {
        board[ourRow][c] = cell;
      }
    }
  }
  return board;
}

export function createInitialChessStateFen(): IChessStateFen {
  return {
    fen: new Chess().fen(),
    moveHistorySan: [],
  };
}

export function getChessLegalMoves(fen: string, square: Square): IMove[] {
  const chess = new Chess(fen);
  return chess.moves({ square, verbose: true }).map((m) => ({
    from: m.from as Square,
    to: m.to as Square,
    promotion: m.promotion as PieceSymbol | undefined,
  }));
}

export function getKingInCheckSquare(fen: string): IRowCol | null {
  const chess = new Chess(fen);
  if (!chess.isCheck()) return null;
  const turn = chess.turn();
  const cb = chess.board();
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const p = cb[r][c];
      if (p?.type === 'k' && p.color === turn) {
        return { row: 7 - r, col: c };
      }
    }
  }
  return null;
}
